import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { CurrentUser, DataRoomDiligenceRequest } from '../../shared/models';

interface DiligenceRequestActionButtonProps {
	currentUser: CurrentUser;
	isTeamMember: boolean;
	request: DataRoomDiligenceRequest;
	onEdit: (request: DataRoomDiligenceRequest) => void;
	onDelete: (request: DataRoomDiligenceRequest) => void;
	onViewDetails: (request: DataRoomDiligenceRequest) => void;
	onAssociate: (request: DataRoomDiligenceRequest) => void;
}

type DiligenceActionItem = keyof typeof DiligenceActionItemMap;
const DiligenceActionItemMap = {
	Edit: 'Edit',
	ViewDetails: 'View Details',
	Delete: 'Delete',
	AssociateDocuments: 'Associate Documents',
};

export const DiligenceRequestActionButton: React.FC<DiligenceRequestActionButtonProps> = ({
	currentUser,
	isTeamMember,
	request,
	onEdit,
	onDelete,
	onViewDetails,
	onAssociate,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const canEdit = getCanEditPermission(currentUser, isTeamMember, request);
	const canDelete = getCanDeletePermission(currentUser, isTeamMember, request);
	const canAssociate = getCanAssociatePermission(request);

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: DiligenceActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(request);
				break;
			case 'ViewDetails':
				onViewDetails(request);
				break;
			case 'Delete':
				onDelete(request);
				break;
			case 'AssociateDocuments':
				onAssociate(request);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: DiligenceActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{canEdit && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
											{currentUser.isCompanyUser ? 'Respond' : DiligenceActionItemMap['Edit']}
										</MenuItem>
									)}
									{canAssociateAction(canAssociate, currentUser, isTeamMember) && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AssociateDocuments')}>
											{DiligenceActionItemMap['AssociateDocuments']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'ViewDetails')}>
										{DiligenceActionItemMap['ViewDetails']}
									</MenuItem>
									{canDelete && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
											{DiligenceActionItemMap['Delete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

function getCanEditPermission(
	currentUser: CurrentUser,
	isTeamMember: boolean,
	request: DataRoomDiligenceRequest
): boolean {
	const isBankUserOrTeamMember = currentUser.isBankUser || isTeamMember;
	const isRequestCreator = request.createdByGuid === currentUser.userGuid;

	if (request.isWithdrawn) {
		return false;
	}

	if (request.isCompleted && isBankUserOrTeamMember) {
		return false;
	}

	if (!request.isCompleted && isBankUserOrTeamMember && !isRequestCreator) {
		return false;
	}

	return true;
}

function getCanDeletePermission(
	currentUser: CurrentUser,
	isTeamMember: boolean,
	request: DataRoomDiligenceRequest
): boolean {
	const isBankUserOrTeamMember = currentUser.isBankUser || isTeamMember;
	let canDelete = getCanEditPermission(currentUser, isTeamMember, request);

	const hasDetails = getHasDetailsFlag(request);
	if (isBankUserOrTeamMember && hasDetails) {
		canDelete = false;
	}

	return canDelete;
}

function getHasDetailsFlag(request: DataRoomDiligenceRequest): boolean {
	return (request.resolution && request.resolution.length > 0) || (request.files && request.files.length > 0);
}

function getCanAssociatePermission(request: DataRoomDiligenceRequest): boolean {
	let canAssociate = true;

	if (request.isCompleted || request.isWithdrawn) {
		canAssociate = false;
	}
	if (request.diligenceType !== 'DocumentRequest') {
		canAssociate = false;
	}

	return canAssociate;
}

function canAssociateAction(canAssociate: boolean, currentUser: CurrentUser, isTeamMember: boolean): boolean {
	let canPermission = canAssociate;
	const isBankUserOrTeamMember = currentUser.isBankUser || isTeamMember;

	if (isBankUserOrTeamMember) {
		canPermission = false;
	}

	return canPermission;
}
